import type { AttachmentOut, CorrespondenceOut } from "@/api";
import { Button } from "@/components/Button";
import { Dialog, DialogFooter } from "@/components/Dialog";
import { formatBytes } from "@/lib/format";

import { adoptCandidates, splitAttachments } from "./attachments";
import styles from "./Record.module.css";
import { BeiwerkFold } from "./TimelineEntry";

/**
 * One file to adopt, as a chip of the record's kind (name · size, the whole
 * of it in `title`) — a button that takes it. Named as the menu item it
 * replaces, „„…“ als Zeichnung übernehmen", so a list of them read on its
 * own still says what each one does.
 */
function PickChip({
  attachment,
  first = false,
  onChoose,
}: {
  attachment: AttachmentOut;
  /** Takes the focus when the dialog opens — the likely drawing. */
  first?: boolean;
  onChoose: (attachment: AttachmentOut) => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.chip} ${styles.chipPick}`}
      title={`${attachment.filename} · ${formatBytes(attachment.byteSize)}`}
      aria-label={`„${attachment.filename}“ als Zeichnung übernehmen`}
      data-autofocus={first ? "" : undefined}
      onClick={() => onChoose(attachment)}
    >
      <span className={styles.chipName}>{attachment.filename}</span>
      <span className={`tabular ${styles.chipSize}`}>{formatBytes(attachment.byteSize)}</span>
    </button>
  );
}

/**
 * „Bild als Zeichnung übernehmen …" (UX-Gutachten 2, B-1): which of a mail's
 * files is the drawing. The entry's menu listed one item per file — twenty
 * for a mail with a sketch and sixteen signature pictures, and it lay over
 * the masthead. From three files on (`ADOPT_ITEMS_MAX`) the menu has one
 * item, and this asks.
 *
 * The files as the entry shows them (design 2026-10-24 §6.2, §8.5): the
 * pictures that may be a drawing in sight, the Beiwerk folded behind
 * „+ 16 Signaturbilder" — every one of them still adoptable, nothing is
 * lost. Names and sizes, no thumbnails: the attachments of a mail have no
 * endpoint that draws them inline — a page of a drawing is drawn only once it
 * is filed under „Zeichnungen" — so the chooser names them, as the entry's
 * chips do (final review, M-9).
 *
 * A chip takes its file at once, the way the menu item did: the dialog
 * closes — the focus back on the menu's button — and `onChoose` adopts it
 * (`adoptIntoDrawings`), the answer said at the entry and aloud. The first
 * file in sight takes the focus, so Enter takes the likely drawing.
 */
export function AdoptDialog({
  entry,
  onChoose,
  onClose,
}: {
  entry: CorrespondenceOut;
  onChoose: (attachment: AttachmentOut) => void;
  onClose: () => void;
}) {
  // Every candidate is still adoptable, so none is a drawing yet: the split
  // is by size and kind alone.
  const { shown, beiwerk } = splitAttachments(adoptCandidates(entry), () => false);
  return (
    <Dialog title="Als Zeichnung übernehmen" subtitle={entry.subject ?? entry.sourceFilename ?? "Mail"} onClose={onClose}>
      <div className={styles.dialogForm}>
        <p className={styles.plain}>
          Welcher Anhang ist die Zeichnung? Er liegt danach als Kopie unter Zeichnungen, die Mail bleibt in der Akte.
        </p>
        {shown.length > 0 ? (
          <ul className={styles.chips} aria-label="Anhänge">
            {shown.map((attachment, index) => (
              <li key={attachment.id}>
                <PickChip attachment={attachment} first={index === 0} onChoose={onChoose} />
              </li>
            ))}
          </ul>
        ) : null}
        {beiwerk.length > 0 ? (
          <BeiwerkFold
            beiwerk={beiwerk}
            // Nothing else to choose from: open, or the dialog would hold
            // a toggle and nothing in sight.
            defaultOpen={shown.length === 0}
            chip={(attachment) => <PickChip attachment={attachment} onChoose={onChoose} />}
          />
        ) : null}
        <DialogFooter>
          <Button onClick={onClose}>Abbrechen</Button>
        </DialogFooter>
      </div>
    </Dialog>
  );
}
